/**
 * #1513 — "Completed 2:14 PM · reached us 4:40 PM".
 *
 * Since #1512 a tech can finish a job with no signal: the paperwork queues on
 * his phone and delivers when it next reaches a tower. `jobs.completed_at` is
 * therefore the moment he TAPPED (trust-checked server-side), and
 * `completed_received_at` is set only when the delivery came later.
 *
 * Two different facts, and the office needs both:
 *   • the completion time is what payroll, points and the day's record run on;
 *   • the arrival time explains why the job appeared on the board late, so
 *     nobody reads a 2:14 job as a 4:40 one, or as a tech who dawdled.
 *
 * Renders nothing for the ordinary case — a job completed online has no
 * arrival lag, and a row saying "reached us at the same moment" would be noise.
 */
import { Clock } from "lucide-react";

function timeOfDay(iso: string): string {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function sameDay(a: string, b: string): boolean {
    const x = new Date(a);
    const y = new Date(b);
    return x.toDateString() === y.toDateString();
}

export function ArrivalLag({
    completedAt,
    completedReceivedAt,
}: {
    completedAt?: string | null;
    completedReceivedAt?: string | null;
}) {
    if (!completedAt || !completedReceivedAt) return null;
    // Guard the degenerate case: if the two are effectively the same instant
    // there is nothing to explain.
    const gapMs = new Date(completedReceivedAt).getTime() - new Date(completedAt).getTime();
    if (!Number.isFinite(gapMs) || gapMs < 60_000) return null;

    // A next-day arrival is the one that actually confuses people, so it says
    // the date rather than a bare time.
    const arrived = sameDay(completedAt, completedReceivedAt)
        ? timeOfDay(completedReceivedAt)
        : new Date(completedReceivedAt).toLocaleString([], {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
          });

    return (
        <div
            data-testid="arrival-lag"
            className="flex items-center gap-1.5 text-xs text-foreground/70"
            title="The tech finished out of coverage; his phone delivered the paperwork when it found signal."
        >
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>
                Finished <span className="font-medium text-foreground">{timeOfDay(completedAt)}</span> · reached us{" "}
                {arrived}
            </span>
        </div>
    );
}

/**
 * #1513 — "Brian's phone last reached us 1:50 PM".
 *
 * The honest half of the offline story. While a tech's paperwork is still on
 * his phone we genuinely do not know whether he has finished; claiming
 * otherwise would be inventing data. What we can say is when his app last
 * reached us, which is enough for the office to tell "still working" from
 * "finished, out of coverage" without phoning him.
 *
 * Deliberately quiet: it renders only while the job is actually being worked
 * AND the tech has been out of contact long enough to be worth remarking on.
 * A tech whose phone checked in a minute ago is simply working, and a row
 * saying so every time would train people to ignore the row.
 */
const WORKING_STATUSES = new Set(["en_route", "arrived", "in_progress"]);
/** Below this, being out of contact is unremarkable. */
const QUIET_FOR_MS = 10 * 60 * 1000;

export function LastReachedUs({
    status,
    lastSeenAt,
    techName,
}: {
    status?: string | null;
    lastSeenAt?: string | null;
    techName?: string | null;
}) {
    if (!status || !WORKING_STATUSES.has(status)) return null;
    if (!lastSeenAt) return null;
    const quietMs = Date.now() - new Date(lastSeenAt).getTime();
    if (!Number.isFinite(quietMs) || quietMs < QUIET_FOR_MS) return null;

    const who = techName?.trim() ? `${techName.trim()}'s phone` : "This tech's phone";
    return (
        <div
            data-testid="last-reached-us"
            className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400"
            title="He may be mid-job or finished in a dead zone — his paperwork sends itself when the phone finds signal."
        >
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>
                {who} last reached us <span className="font-medium">{timeOfDay(lastSeenAt)}</span>
            </span>
        </div>
    );
}
